import { Injectable, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { from, Observable, switchMap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie';
import { environment } from 'src/environments/environment';
import { AnalyticsAuthService } from '../services/analytics-auth.service';

export const ANALYTICS_TOKEN_COOKIE = 'analytics_token';

/**
 * Для запросов на analytics-сервис:
 *  - ставит Bearer JWT из cookie;
 *  - удаляет Authorization, проставленный глобальным main-интерсептором —
 *    иначе main-токен metadoor улетал бы в чужой сервис и получал 401;
 *  - при 401 на не-login-запросе пробует продлить сессию (silentLogin через
 *    зашифрованные креды из sessionStorage) и повторяет исходный запрос.
 *
 * Через Injector берём AnalyticsAuthService лениво, чтобы не было кольца
 * DI (AuthService → AnalyticsAuthService → HttpClient → этот interceptor).
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsAuthInterceptor implements HttpInterceptor {
  constructor(
    private readonly _cookieService: CookieService,
    private readonly _injector: Injector,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Дополнительный страховочный чек на случай, если environment.analyticsApiUrl
    // вырождается в 'https://' (CI не подставил placeholder). Без него startsWith
    // совпадёт со ВСЕМИ https-запросами и interceptor сломает основное приложение.
    if (!environment.analyticsApiUrl.includes('.')) {
      return next.handle(req);
    }
    if (!req.url.startsWith(environment.analyticsApiUrl)) {
      return next.handle(req);
    }

    const authed = this._withAuth(req);
    return next.handle(authed).pipe(
      catchError((err: unknown) => {
        if (
          err instanceof HttpErrorResponse &&
          err.status === 401 &&
          !req.url.includes('/auth/login')
        ) {
          // Лениво резолвим сервис — на момент конструирования интерсептора
          // он может ещё не быть готов из-за кольца зависимостей.
          const auth = this._injector.get(AnalyticsAuthService);
          return from(auth.refreshFromPersisted()).pipe(
            switchMap((ok) => {
              if (!ok) return throwError(() => err);
              // refresh положил новый токен в cookie → пересобираем запрос.
              return next.handle(this._withAuth(req));
            }),
          );
        }
        return throwError(() => err);
      }),
    );
  }

  private _withAuth(req: HttpRequest<unknown>): HttpRequest<unknown> {
    let headers = req.headers.delete('Authorization');
    if (!req.url.includes('/auth/login')) {
      const token = this._cookieService.get(ANALYTICS_TOKEN_COOKIE);
      if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return req.clone({ headers });
  }
}
